import type { StateCreator } from 'zustand';
import type { Post, KanbanColumn } from '../types';
import { contentApi } from '../services/contentApi';

export interface PostsSlice {
  posts: Post[];
  postsLoading: boolean;
  kanban: Record<KanbanColumn, Post[]>;
  imageTexts: Record<number, string>;
  rewrites: Record<number, string>;
  rewriteHashtags: Record<number, string>;
  selectedPostId: number | null;
  fetchPosts: (niche: string) => Promise<void>;
  scrapeNewPosts: (niche: string) => Promise<{ new_count: number }>;
  extractImageText: (postId: number) => Promise<string | null>;
  rewriteCaption: (postId: number, niche: string, isRegen?: boolean) => Promise<void>;
  moveToColumn: (postId: number, target: KanbanColumn) => void;
  setSelectedPost: (postId: number | null) => void;
  assignPost: (postId: number) => Promise<void>;
  completePost: (postId: number) => Promise<void>;
  fetchPostDetail: (postId: number) => Promise<void>;
}

export const createPostsSlice: StateCreator<PostsSlice> = (set, get) => ({
  posts: [],
  postsLoading: false,
  kanban: { scraped: [], ready: [], assigned: [], done: [] },
  imageTexts: {},
  rewrites: {},
  rewriteHashtags: {},
  selectedPostId: null,

  fetchPosts: async (niche) => {
    set({ postsLoading: true });
    try {
      const posts = await contentApi.listPosts(niche, 'viral', 50);
      const scraped: Post[] = [];
      const ready: Post[] = [];
      const assigned: Post[] = [];
      const done: Post[] = [];
      for (const p of posts) {
        if (p.used_at || p.queue_status === 'posted') {
          done.push(p);
        } else if (p.queue_status === 'pending' || p.queue_status === 'sent') {
          assigned.push(p);
        } else if (p.has_rewrite) {
          ready.push(p);
        } else {
          scraped.push(p);
        }
      }
      set({ posts, kanban: { scraped, ready, assigned, done }, postsLoading: false });
    } catch {
      set({ postsLoading: false });
    }
  },

  scrapeNewPosts: async (niche) => {
    set({ postsLoading: true });
    try {
      const result = await contentApi.scrape(niche);
      // Refresh posts after scrape
      await get().fetchPosts(niche);
      return { new_count: result.new_count ?? 0 };
    } catch {
      set({ postsLoading: false });
      return { new_count: 0 };
    }
  },

  extractImageText: async (postId) => {
    const res = await contentApi.ocr(postId);
    if (res.image_text) {
      set((s) => ({ imageTexts: { ...s.imageTexts, [postId]: res.image_text! } }));
    }
    return res.image_text;
  },

  rewriteCaption: async (postId, niche, isRegen = false) => {
    const res = await contentApi.rewrite(postId, niche, isRegen);
    set((s) => ({
      rewrites: { ...s.rewrites, [postId]: res.rewritten_text },
      rewriteHashtags: res.hashtags
        ? { ...s.rewriteHashtags, [postId]: res.hashtags }
        : s.rewriteHashtags,
    }));
    // Move to ready column
    get().moveToColumn(postId, 'ready');
  },

  moveToColumn: (postId, target) => {
    set((s) => {
      const newKanban = { ...s.kanban };
      let movedPost: Post | undefined;
      // Remove from current column
      for (const col of Object.keys(newKanban) as KanbanColumn[]) {
        const idx = newKanban[col].findIndex((p) => p.id === postId);
        if (idx >= 0) {
          movedPost = newKanban[col][idx];
          newKanban[col] = [...newKanban[col].slice(0, idx), ...newKanban[col].slice(idx + 1)];
          break;
        }
      }
      if (movedPost) {
        newKanban[target] = [...newKanban[target], movedPost];
      }
      return { kanban: newKanban };
    });
  },

  setSelectedPost: (postId) => set({ selectedPostId: postId }),

  assignPost: async (postId) => {
    await contentApi.assignPost(postId);
    get().moveToColumn(postId, 'assigned');
  },

  completePost: async (postId) => {
    await contentApi.completePost(postId);
    get().moveToColumn(postId, 'done');
  },

  fetchPostDetail: async (postId) => {
    const detail = await contentApi.getPostDetail(postId);
    if (detail.image_text) {
      set((s) => ({ imageTexts: { ...s.imageTexts, [postId]: detail.image_text! } }));
    }
    if (detail.rewrite) {
      set((s) => ({
        rewrites: { ...s.rewrites, [postId]: detail.rewrite!.rewritten_text },
        rewriteHashtags: detail.rewrite!.hashtags
          ? { ...s.rewriteHashtags, [postId]: detail.rewrite!.hashtags! }
          : s.rewriteHashtags,
      }));
    }
  },
});
